import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Snackbar,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { useLazyPrintPayConfirmQuery } from '@/store/api/payDataApi'
import { printAndUploadReport } from '@/utils/printAndUploadReport'
import { HIDE_ON_MOBILE_INLINE, PRINT_PREVIEW_DIALOG_PAPER, TEXT_TRUNCATE } from '@/theme/responsive'

// Access port: btnXNTTver2 "Print - XNTT" → rpProcurementPayConfirm_* (Pay Confirm).
// BE chọn variant theo row (pay_data_confirm_print). Self-fetching dialog: lazy-fires
// the print query on open, renders returned `html` into an iframe srcDoc + Print button.
// Mirrors ProcurementLiquidPrintDialog.

interface Props {
  open: boolean
  paydataId: number | null
  xnttNo?: string | null
  onClose: () => void
}

const PRINT_PREVIEW_CSS = `
@page { size: A4 !important; margin: 0 !important; }
@media print {
  @page { size: A4 !important; margin: 0 !important; }
  body { margin: 0 !important; }
  .report-page { width: 210mm !important; max-width: none !important; min-height: 297mm !important; padding: 9mm 11mm !important; }
}
@media screen and (max-width: 600px) {
  .report-page { min-height: auto !important; max-width: 100% !important; width: 100% !important; padding: 4mm !important; margin: 0 !important; }
  body { margin: 0 !important; background: #fff !important; }
}
body { margin: 0 !important; }
.print-action { display: none !important; }
.report-page { width: 100% !important; max-width: 200mm !important; padding: 0 !important; min-height: auto !important; margin: 0 auto !important; }
`.trim()

function buildPreviewHtml(rawHtml: string): string {
  const withoutInlinePrint = rawHtml.replace(/<button\s+class="print-action[^>]*>[\s\S]*?<\/button>/g, '')
  const withBase = withoutInlinePrint.replace('<head>', `<head><base href="${window.location.origin}/">`)
  const styleTag = `<style id="payconfirm-print-preview-overrides">${PRINT_PREVIEW_CSS}</style>`
  if (withBase.includes('</head>')) return withBase.replace('</head>', `${styleTag}</head>`)
  return `${styleTag}${withBase}`
}

function sanitizePdfFileNamePart(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, '_')
}

export default function PayDataConfirmPrintDialog({ open, paydataId, xnttNo, onClose }: Props) {
  const [toastMessage, setToastMessage] = useState('')
  const [trigger, { data, isFetching, isError, error }] = useLazyPrintPayConfirmQuery()

  useEffect(() => {
    if (open && paydataId != null) {
      void trigger(paydataId)
    }
  }, [open, paydataId, trigger])

  const rawHtml = data?.data?.html ?? ''
  const previewHtml = rawHtml ? buildPreviewHtml(rawHtml) : ''
  const iframeTitle = 'Pay Confirm Print Preview'
  const label = xnttNo || (paydataId != null ? String(paydataId) : '')

  useEffect(() => {
    if (open && isError) {
      const detail =
        (error as { data?: { detail?: string } } | undefined)?.data?.detail ?? 'Cannot load Pay Confirm.'
      setToastMessage(detail)
    }
  }, [open, isError, error])

  return (
    <>
      {open && isFetching && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 1300, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(0,0,0,0.35)' }}>
          <CircularProgress size={48} />
        </Box>
      )}
      <Dialog open={open && Boolean(rawHtml)} onClose={onClose} maxWidth={false} fullWidth PaperProps={{ sx: PRINT_PREVIEW_DIALOG_PAPER }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 1 }}>
          <Box component="span" title={`Print Preview - XNTT (${label})`} sx={{ ...TEXT_TRUNCATE, flex: '1 1 auto', minWidth: 0, fontWeight: 700, fontSize: '1rem' }}>
            <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Print Preview - </Box>
            XNTT ({label})
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button onClick={onClose} size="small">Cancel</Button>
            <Button
              variant="contained"
              size="small"
              disabled={!rawHtml}
              onClick={() => {
                const printTitle = `${sanitizePdfFileNamePart(label || 'pay_confirm')}_XNTT.pdf`
                void printAndUploadReport({
                  iframeTitle,
                  reportName: 'rpProcurementPayConfirm',
                  printTitle,
                  html: previewHtml,
                  onUploadError: setToastMessage,
                })
              }}
            >
              Print
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ height: '78vh', p: 0 }}>
          <Box component="iframe" title={iframeTitle} srcDoc={previewHtml} sx={{ width: '100%', height: '100%', border: 0, display: 'block', bgcolor: 'white' }} />
        </DialogContent>
      </Dialog>
      <Snackbar
        open={Boolean(toastMessage)}
        message={toastMessage}
        autoHideDuration={2500}
        onClose={() => setToastMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      />
    </>
  )
}
